/**
 * Sieve — divisibility engine for the Multiples mode (spec docs/SPEC-multiples).
 *
 * Part of the shared engine (§14): the Multiples generator, the curation tooling
 * and the live client all import from here. The prime hunt is the limiting case
 * of this family ("divisible by nothing"), so this sits alongside primes.ts and
 * reuses factorize() for the reveal.
 */

import { MIN_NUMBER, MAX_NUMBER, factorize } from './primes';

export function isDivisible(n: number, x: number): boolean {
  return n % x === 0;
}

/** Divisor difficulty bands (§4.1). */
export type Band = 'b1' | 'b2' | 'b3' | 'b4';

export const BANDS: Record<Band, number[]> = {
  b1: [2, 5, 10], // trivial — last digit
  b2: [3, 9], //     easy — digit sum
  b3: [4, 6, 8], //  medium — short division / partial factors
  b4: [7, 11, 13], // hard — no shortcut
};

export function divisorBand(x: number): Band {
  for (const band of ['b1', 'b2', 'b3', 'b4'] as Band[]) {
    if (BANDS[band].includes(x)) return band;
  }
  throw new Error(`divisor ${x} is not in any band`);
}

/** Weekday → band (§4.4). Invariant: Monday ∈ B1, Saturday ∈ B4. */
const WEEKDAY_BAND: Band[] = ['b1', 'b2', 'b3', 'b3', 'b4', 'b4', 'b2']; // Mon…Sun (ISO)

export function bandForDate(isoDate: string): Band {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const jsDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun…6=Sat
  const iso = (jsDay + 6) % 7; // 0=Mon…6=Sun
  return WEEKDAY_BAND[iso]!;
}

/**
 * Picks the specific divisor for a date: the weekday sets the band, and the
 * week index rotates through the band so consecutive same-weekdays vary
 * (§4.4 — "rotating across weeks so Mondays are not all ÷2"). `weekIndex` is the
 * number of whole weeks since the queue start.
 */
export function divisorForDate(isoDate: string, weekIndex: number): number {
  const band = bandForDate(isoDate);
  const pool = BANDS[band];
  return pool[((weekIndex % pool.length) + pool.length) % pool.length]!;
}

/** All multiples of x in range — the target pool. */
export function multiplesInRange(x: number, min = MIN_NUMBER, max = MAX_NUMBER): number[] {
  const out: number[] = [];
  const start = Math.ceil(min / x) * x;
  for (let n = start; n <= max; n += x) out.push(n);
  return out;
}

/** Distinct prime factors of x — used to build partial-factor distractors. */
export function primeFactorsOf(x: number): number[] {
  return [...new Set(factorize(x))];
}

export type NonMultipleClass = 'obvious' | 'nearMiss' | 'partialFactor';

/**
 * Classifies a NON-multiple cell relative to the divisor and the grid's actual
 * targets (§4.2). Grid-aware because "near-miss" means numerically adjacent to a
 * real answer. Precedence: near-miss ▸ partial-factor ▸ obvious. Partial-factor
 * only exists for B3 divisors (§4.3), where sharing a proper factor of x is the
 * tempting shortcut trap; for other bands a factor-sharing number is simply
 * "obvious".
 */
export function classifyNonMultiple(
  n: number,
  x: number,
  targets: readonly number[],
  band = divisorBand(x),
): NonMultipleClass {
  for (const t of targets) {
    if (Math.abs(n - t) <= 2) return 'nearMiss';
  }
  if (band === 'b3' && gcd(n, x) > 1) return 'partialFactor';
  return 'obvious';
}

export function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

/**
 * The wrong-tap / reveal teaching string (§6.3):
 *   • a target renders as the product      714 → "7×102"
 *   • a non-multiple renders as remainder   344 → "7×49 + 1"
 * This is the mode's lesson: how far a number sits from the nearest multiple.
 */
export function divisionReveal(n: number, x: number): string {
  const q = Math.floor(n / x);
  const r = n % x;
  return r === 0 ? `${x}×${q}` : `${x}×${q} + ${r}`;
}
